/**
 * Performance Monitor Utility
 *
 * Simple performance monitoring for identifying bottlenecks.
 * Enable/disable via localStorage: localStorage.setItem('DEBUG_PERF', 'true')
 */

const isEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('DEBUG_PERF') === 'true';
};

const timers: Map<string, number> = new Map();
const measurements: Map<string, number[]> = new Map();

/**
 * Start a performance timer
 */
export const perfStart = (label: string): void => {
  if (!isEnabled()) return;
  timers.set(label, performance.now());
};

/**
 * End a performance timer and log the result
 */
export const perfEnd = (label: string): number => {
  if (!isEnabled()) return 0;

  const start = timers.get(label);
  if (start === undefined) {
    console.warn(`[PERF] No timer found for: ${label}`);
    return 0;
  }

  const duration = performance.now() - start;
  timers.delete(label);

  // Store measurement for averaging
  const existing = measurements.get(label) ?? [];
  existing.push(duration);
  if (existing.length > 100) existing.shift(); // Keep last 100
  measurements.set(label, existing);

  // Log with color coding
  const color = duration > 16 ? 'color: red' : duration > 8 ? 'color: orange' : 'color: green';
  console.log(`%c[PERF] ${label}: ${duration.toFixed(2)}ms`, color);

  return duration;
};

/**
 * Measure a synchronous function
 */
export const perfMeasure = <T>(label: string, fn: () => T): T => {
  if (!isEnabled()) return fn();

  perfStart(label);
  const result = fn();
  perfEnd(label);
  return result;
};

/**
 * Log a render event
 */
export const perfRender = (componentName: string): void => {
  if (!isEnabled()) return;
  console.log(`%c[PERF] Render: ${componentName}`, 'color: #888');
};

/**
 * Get performance summary
 */
export const perfSummary = (): void => {
  if (!isEnabled()) {
    console.log(
      'Performance monitoring disabled. Enable with: localStorage.setItem("DEBUG_PERF", "true")'
    );
    return;
  }

  console.log('\n=== Performance Summary ===');
  measurements.forEach((times, label) => {
    const avg = times.reduce((a, b) => a + b, 0) / times.length;
    const max = Math.max(...times);
    const min = Math.min(...times);
    console.log(`${label}:`, {
      avg: `${avg.toFixed(2)}ms`,
      max: `${max.toFixed(2)}ms`,
      min: `${min.toFixed(2)}ms`,
      samples: times.length,
    });
  });
  console.log('===========================\n');
};

/**
 * Clear all measurements
 */
export const perfClear = (): void => {
  timers.clear();
  measurements.clear();
  console.log('[PERF] Measurements cleared');
};

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>)['perfSummary'] = perfSummary;
  (window as unknown as Record<string, unknown>)['perfClear'] = perfClear;
}
